import React from "react"
import { InputText } from "../Text"
import { Select } from "../Select"
import * as UtilsApi from "features/api/UtilsApi"

const POSTCODE_LENGTH = 5

export const InputCity = ({
    // Attributes
    value = "",
    name,
    label = "",
    disabled,
    readonly,
    postCode = null,
    // Styles
    className = "",
    // Error management
    required = false,
    showErrors,
    // Handler
    onChange,
    onValidate,
    updateInseeCode
}) => {
    const [cityList, setCityList] = React.useState(null)

    const fetchCities = async () => {
        switch (postCode.toString()) {
            case "75000":
                return [{ value: "Paris", label: "Paris", inseeCode: "75000" }]
            case "13000":
                return [{ value: "Marseille", label: "Marseille", inseeCode: "13000" }]
            case "69000":
                return [{ value: "Lyon", label: "Lyon", inseeCode: "69000" }]
            default:
                return await UtilsApi.fetchCityList(postCode).then((list) => {
                    if (list) {
                        return list.map(({ nom, code }) => ({
                            label: nom,
                            value: nom,
                            inseeCode: code,
                        }))
                    } else {
                        return null
                    }
                })
        }
    }

    // Set a list of cities if postal code is filled
    React.useEffect(() => {
        if (!readonly) {
            if (postCode && postCode.length === POSTCODE_LENGTH) {
                ;(async () => {
                    const fetchedCities = await fetchCities()
                    setCityList(fetchedCities)
                })()
            } else {
                setCityList(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postCode])

    // intermediary method to provide the inputcity form's parent with
    // both a city value and an insee code value
    const handleChange = (inputValue) => {
        const selectedCity = inputValue?.target?.value
        const correspondingInseeCode =
            selectedCity && cityList
                ? cityList.find((city) => city.value === selectedCity)?.inseeCode ?? "00000"
                : "00000"
        if (onChange) {
            // Update city (name) value
            onChange(inputValue)
            // If needed, also update insee code (inseeCodename) value
            if (updateInseeCode) updateInseeCode(s => ({
                ...s,
                code_insee: correspondingInseeCode
            }))
        }
    }

    const firstUpdate = React.useRef(true)
    React.useEffect(() => {
        // Clear the input if there is no more postcode
        // Note: cityList is often null by default. We use a ref
        // to keep track of update and prevent the onChange to be
        // triggered on first load.
        if (!cityList && !firstUpdate.current) {
            handleChange({
                target: {
                    name,
                    value: "",
                    label: "",
                },
            })
        }
        if (firstUpdate.current) firstUpdate.current = false

        // Autoselect the one only city in the list
        if (cityList?.length === 1) {
            handleChange({
                target: {
                    name,
                    label: cityList[0].label,
                    value: cityList[0].value,
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityList])

    const commonProps = {
        value,
        name,
        label,
        readonly,
        disabled,
        className,
        required,
        showErrors,
        onChange: handleChange,
        onValidate,
    }

    // If no city, return a basic inputText so the user
    // can fill his/her own city
    if (!cityList || cityList.length <= 1) {
        return <InputText {...commonProps} disabled={cityList?.length === 1 || disabled} />
    }

    return <Select options={cityList} {...commonProps} />
}
